// src/services/geocodingService.js
class GeocodingService {
  constructor() {
    this.apiKey = null;
    this.initialized = false;
  }

  async initialize(apiKey) {
    if (!apiKey) throw new Error('Google Maps API key is required');
    this.apiKey = apiKey;
    
    if (!window.google) {
      await this.loadGoogleMapsScript();
    }
    
    this.geocoder = new window.google.maps.Geocoder();
    this.placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    this.initialized = true;
  }

  loadGoogleMapsScript() {
    return new Promise((resolve, reject) => {
      if (window.google) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async geocodeAddress(address) {
    if (!this.initialized) throw new Error('Geocoding service not initialized');

    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          const location = result.geometry.location;
          
          resolve({
            address: result.formatted_address,
            coordinates: {
              latitude: location.lat(),
              longitude: location.lng()
            },
            placeId: result.place_id,
            addressComponents: this.parseAddressComponents(result.address_components)
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }

  async reverseGeocode(lat, lng) {
    if (!this.initialized) throw new Error('Geocoding service not initialized');

    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ 
        location: { lat: parseFloat(lat), lng: parseFloat(lng) } 
      }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          resolve({
            address: result.formatted_address,
            addressComponents: this.parseAddressComponents(result.address_components)
          });
        } else {
          reject(new Error(`Reverse geocoding failed: ${status}`));
        }
      });
    });
  }

  parseAddressComponents(components) {
    const parsed = {};
    
    components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        parsed.streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        parsed.route = component.long_name;
      }
      if (types.includes('locality')) {
        parsed.city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        parsed.province = component.long_name;
      }
      if (types.includes('postal_code')) {
        parsed.postalCode = component.long_name;
      }
      if (types.includes('country')) {
        parsed.country = component.long_name;
      }
    });
    
    return parsed;
  }
}

export const geocodingService = new GeocodingService();